import { motion } from "framer-motion";
import {
  Check,
  FileSpreadsheet,
  Users,
  LineChart,
  ClipboardCheck,
} from "lucide-react";
import Reveal from "@/components/landing/Reveal";

const FEATURES = [
  {
    no: "01",
    icon: FileSpreadsheet,
    title: "AI Product Import Assistant",
    points: [
      "Validates Excel files before importing products.",
      "Detects missing fields, duplicates, and invalid data instantly.",
      "Suggests corrections to ensure clean and error-free imports.",
    ],
  },
  {
    no: "02",
    icon: Users,
    title: "AI Customer Intelligence",
    points: [
      "Learns customer payment behaviour and purchase patterns.",
      "Identifies inactive customers and seasonal buying trends.",
      "Recommends the next best follow-up for every customer.",
    ],
  },
  {
    no: "03",
    icon: LineChart,
    title: "AI Dashboard Insights",
    points: [
      "Continuously analyses live business data in real time.",
      "Explains sales trends, payment patterns, and business performance.",
      "Highlights risks, opportunities, and key insights automatically.",
    ],
  },
  {
    no: "04",
    icon: ClipboardCheck,
    title: "AI Business Review",
    points: [
      "Analyses complete customer transaction history automatically.",
      "Compares monthly and yearly performance with actionable insights.",
      "Identifies high-value customers and growth opportunities.",
    ],
  },
];

const FeatureCard = ({ item, index }) => {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        duration: 0.6,
        delay: (index % 2) * 0.08,
        ease: [0.22, 1, 0.36, 1],
      }}
      data-testid={`ai-feature-card-${item.no}`}
      className="group relative overflow-hidden rounded-2xl border border-black/[0.07] bg-white p-8 transition-colors duration-300 hover:border-brand-teal/40"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(80% 80% at 100% 0%, rgba(13,148,136,0.06) 0%, rgba(255,255,255,0) 60%)",
        }}
      />
      <div className="relative flex items-start justify-between">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#fafafa] text-[#0a0a0a] ring-1 ring-black/[0.05] transition-colors duration-300 group-hover:bg-brand-teal/10 group-hover:text-brand-teal">
          <Icon className="h-5 w-5" />
        </span>
        <span className="font-mono text-xs text-[#d4d4d8]">{item.no}</span>
      </div>

      <h3 className="relative mt-8 font-heading text-xl font-bold tracking-tight text-[#0a0a0a]">
        {item.title}
      </h3>

      <ul className="relative mt-5 space-y-3">
        {item.points.map((point, i) => (
          <li
            key={i}
            className="flex items-start gap-2.5 text-sm leading-relaxed text-[#52525b]"
          >
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

export const AIFeatures = () => {
  return (
    <section
      id="ai-features"
      data-testid="ai-features-section"
      className="relative scroll-mt-20 bg-[#fafafa] px-6 py-24 lg:px-10 lg:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <Reveal className="max-w-2xl">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-teal">
            AI Features
          </span>
          <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-[#0a0a0a] sm:text-5xl">
            Intelligence, woven throughout.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#52525b] sm:text-lg">
            The AI capabilities that power AMRA AI Business OS — built to turn
            everyday operations into smarter business decisions.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2">
          {FEATURES.map((item, index) => (
            <FeatureCard key={item.no} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AIFeatures;
